import React from 'react';
import { Tags, X } from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { TagPicker } from '@/client/components/admin/tags/TagPicker';

interface Props {
  open: boolean;
  onClose: () => void;
  value: number[];
  onChange: (tagids: number[]) => void;
  match: 'any' | 'all';
  onMatchChange: (mode: 'any' | 'all') => void;
}

/**
 * Side drawer for the tag filter — a match (any/all) toggle plus the searchable
 * multi-select TagPicker. Selections apply live; "Done" just closes.
 */
const TagFilterDrawer: React.FC<Props> = ({ open, onClose, value, onChange, match, onMatchChange }) => {
  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" className="flex w-full max-w-md flex-col p-0 sm:max-w-md">
        <SheetHeader className="border-b border-gray-100 px-5 py-4 text-left">
          <SheetTitle className="flex items-center gap-2 text-base">
            <Tags className="h-4 w-4 text-indigo-500" />
            Filter by tags
          </SheetTitle>
          <SheetDescription className="text-xs">
            {value.length > 0 ? `${value.length} tag${value.length > 1 ? 's' : ''} selected` : 'No tags selected'}
          </SheetDescription>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto p-5">
          {/* Match mode */}
          <div className="mb-4 flex items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <span className="text-xs font-medium uppercase tracking-wide text-gray-500">Match</span>
              <div className="inline-flex overflow-hidden rounded-md border border-gray-200 bg-white">
                {(['any', 'all'] as const).map((mode) => (
                  <button
                    key={mode}
                    type="button"
                    onClick={() => onMatchChange(mode)}
                    className={`px-3 py-1 text-xs font-medium capitalize transition-colors ${
                      match === mode ? 'bg-blue-600 text-white' : 'text-gray-600 hover:bg-gray-50'
                    }`}
                    title={
                      mode === 'any'
                        ? 'Show listings with any of the selected tags'
                        : 'Show only listings with all of the selected tags'
                    }
                  >
                    {mode}
                  </button>
                ))}
              </div>
            </div>
            {value.length > 0 && (
              <button
                type="button"
                onClick={() => onChange([])}
                className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-gray-800"
              >
                <X className="h-3.5 w-3.5" />
                Clear
              </button>
            )}
          </div>

          <TagPicker value={value} onChange={onChange} placeholder="Search tags by name…" />
        </div>

        <div className="border-t border-gray-100 px-5 py-4">
          <Button className="w-full" onClick={onClose}>
            Done
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default TagFilterDrawer;
